import './index.scss';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Col, FormFeedback, Input, Label, Row } from 'reactstrap';
import { emailValidation } from '../../../../utility/functions/emailValidation';
import { useSpacesManagementCompanySpaces } from '../../../../store/SpacesManagement/spacesManagementHooks';
import { InviteRepeatableVisitorFormValues } from '../../../modals/InviteRepeatableVisitorModal';
import PhoneInput from '../../../shared/PhoneInput/PhoneInput';
import DatePicker from '../../../shared/Datepicker';

type PropsTypes = {
  form: UseFormReturn<InviteRepeatableVisitorFormValues, object>;
  isDuplicate?: boolean;
};

export const InviteRepeatableGuestForm = (props: PropsTypes) => {
  const { t } = useTranslation();
  const companySpaces = useSpacesManagementCompanySpaces();

  const spaceOptions = companySpaces.map((space) => {
    return {
      value: space.id,
      label: `Building : ${space.building.name}, floor : ${space.floor?.number}, space : ${space.name}`,
    };
  });

  const {
    control,
    formState: { errors },
  } = props.form;

  const fullNameValidation = (value?: string): any => {
    const trimValue = value?.trim();
    return (
      trimValue?.replace(' ', '').length !== trimValue?.length ||
      t('FULL_NAME_VALIDATION')
    );
  };

  const guestContactInformationRequired = () => {
    return (
      !!props.form.watch('email') ||
      !!props.form.watch('phone') ||
      t('FORM_ERRORS_FIELD__OR_FIELD_IS_REQUIRED', {
        field1: t('FORM_EMAIL_PLACEHOLDER'),
        field2: t('FORM_PHONE_NUMBER_PLACEHOLDER'),
      })
    );
  };

  const dayInMs = 24 * 60 * 60 * 1000;

  return (
    <>
      <Row>
        <Col md="6" className="form-user-input">
          <Label for="fullName">
            <span>* </span>
            {t('VISITOR_NAME_LABEL')}
          </Label>
          <Controller
            name="fullName"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
              },
              pattern: {
                value: /^[0-9a-zA-Z _"'.\u0590-\u05FF\u200f\u200e]*$/,
                message: t('FORM_ERRORS_INVALID_USE_LETTERS_NUMBER'),
              },
              validate: {
                fullName: (e) => fullNameValidation(e),
              },
            }}
            render={({ field }) => (
              <Input
                id="fullName"
                autoComplete="off"
                type="text"
                className="text-input"
                placeholder={t('NAME_LABEL')}
                invalid={errors.fullName && true}
                {...field}
              />
            )}
          />
          {errors.fullName ? (
            <FormFeedback className="form-input-error">
              {errors.fullName.message}
            </FormFeedback>
          ) : null}
        </Col>
        <Col md="6" className="form-user-input">
          <Label for="email">{t('VISITOR_EMAIL_LABEL')}</Label>
          <Controller
            name="email"
            control={control}
            rules={{
              validate: {
                requiredPatter: () => guestContactInformationRequired(),
                emailPattern: (e) =>
                  emailValidation(e, t('FORM_ERRORS_INVALID_EMAIL'), true),
              },
            }}
            render={({ field }) => (
              <Input
                id="email"
                autoFocus
                type="email"
                placeholder={t('VISITOR_EMAIL_LABEL')}
                invalid={errors.email && true}
                {...field}
              />
            )}
          />
          {errors.email ? (
            <FormFeedback className="form-input-error">
              {errors.email.message}
            </FormFeedback>
          ) : null}
        </Col>
        <Col md="6" className="form-user-input">
          <PhoneInput
            label={t('VISITOR_PHONE_LABEL')}
            placeholder={t('VISITOR_PHONE_LABEL')}
            control={control as any}
            form={props.form as any}
            controlName={'phone'}
            rules={{
              validate: {
                requiredPatter: () => guestContactInformationRequired(),
              },
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col md="6" className="form-user-input">
          <Label for="startDate">
            <span>* </span>
            {t('START_DATE')}
          </Label>
          <Controller
            name="startDate"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
              },
            }}
            render={({ field }) => (
              <DatePicker
                id="startDate"
                className="text-input"
                placeholder={t('START_DATE')}
                minDate={'today'}
                {...field}
              />
            )}
          />
          {errors.startDate ? (
            <FormFeedback className="form-input-error">
              {(errors.startDate as any).message}
            </FormFeedback>
          ) : null}
        </Col>
        <Col md="6" className="form-user-input">
          <Label for="endDate">
            <span>* </span>
            {t('END_DATE')}
          </Label>
          <Controller
            name="endDate"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
              },
            }}
            render={({ field }) => (
              <DatePicker
                id="endDate"
                className="text-input"
                placeholder={t('DATE_LABEL')}
                minDate={
                  props.form.watch('startDate')?.length > 0
                    ? props.form.watch('startDate')[0].getTime() + dayInMs
                    : new Date().getTime() + dayInMs
                }
                maxDate={new Date().setMonth(new Date().getMonth() + 3)}
                {...field}
              />
            )}
          />
          {errors.endDate ? (
            <FormFeedback className="form-input-error">
              {(errors.endDate as any).message}
            </FormFeedback>
          ) : null}
        </Col>
      </Row>
      <Row>
        {spaceOptions.length > 1 && (
          <Col md="12" className="form-user-input">
            <Label for="space">
              <span>* </span>
              {t('PLACE')}
            </Label>
            <Controller
              control={props.form.control}
              rules={{
                required: {
                  value: true,
                  message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
                },
              }}
              name="spaceId"
              render={({ field: { onChange, value } }) => (
                <Select
                  value={spaceOptions.filter((c) => +value === +c.value)}
                  id="space"
                  onChange={(val) => {
                    onChange(val?.value);
                  }}
                  options={spaceOptions}
                  classNamePrefix="select"
                />
              )}
            />
            {errors.spaceId ? (
              <FormFeedback className="form-input-error">
                {t('FORM_ERRORS_FIELD_IS_REQUIRED')}
              </FormFeedback>
            ) : null}
          </Col>
        )}
      </Row>
    </>
  );
};
